<script setup lang="ts">
import {computed} from 'vue';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import {getVueLink} from '@/ts/composables/stateful/use-route-params';
import {pagesPath} from '@/ts/router/pages-path';
import type {TileProgram} from '@/ts/types/component/today-page';

/**
 * One program in the tile: thumbnail, name, and either its progress or, once
 * finished, a completed marker with a link to its summary.
 */
const props = defineProps<{
  program: TileProgram;
}>();

/**
 * The row was opened (IPD-2974). Announced although it is a link and navigates on its own, the
 * way the Today cards do: the tile's whole purpose is to send people into their programs, so
 * counting only the controls that need a handler would count almost none of it.
 */
const emit = defineEmits<{open: [program: TileProgram]}>();

/**
 * A finished program leads to its recap; one in progress leads into the
 * intervention itself. A real link rather than a click handler, so the row can
 * be opened in a new tab and reads as a link to assistive tech.
 */
const target = computed(() => (props.program.completed
  ? getVueLink(pagesPath.intervention.interventionRecap, {iid: props.program.interventionId})
  : getVueLink(pagesPath.intervention.interventionLanding, {iid: props.program.interventionId})));

/**
 * 0..1 in the model, a percentage on screen. Guarded on both ends: NaN survives
 * Math.min/Math.max untouched, and `width: NaN%` is dropped as invalid, which
 * renders a *full* bar — an unfinished program would look finished.
 */
const percent = computed(() => {
  const raw = Number.isFinite(props.program.progress) ? props.program.progress : 0;
  return Math.round(Math.min(1, Math.max(0, raw)) * 100);
});

const thumbStyle = computed(() => (props.program.image
  ? {backgroundImage: `url("${calculateMediaUrl(props.program.image)}")`}
  : {}));
</script>

<template>
  <router-link
    class="pr-row"
    :class="{'pr-row--done': program.completed}"
    :to="target"
    @click="emit('open', program)"
  >
    <span class="pr-thumb" :class="{'pr-thumb--done': program.completed}" :style="thumbStyle" aria-hidden="true">
      <svg
        v-if="program.completed"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    </span>
    <span class="pr-meta">
      <span class="pr-name">{{ program.name }}</span>

      <span v-if="program.completed" class="pr-state">
        <span class="pr-done-label">
          <svg
            class="pr-check"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {{ $t('program_tile_completed') }}
        </span>
      </span>
      <span v-else class="pr-state">
        <span class="pr-bar" aria-hidden="true">
          <i :style="{width: `${percent}%`}"/>
        </span>
        <span class="pr-pct">
          <span>{{ $t('program_tile_percent', {percent}) }}</span>
          <span>{{ $t('program_tile_complete') }}</span>
        </span>
      </span>

      <span v-if="program.completed" class="pr-summary">
        {{ $t('program_tile_view_summary') }}
      </span>
      <span v-else class="pr-go" aria-hidden="true">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        ><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </span>
    </span>
  </router-link>
</template>

<style scoped lang="scss">
.pr-row {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 76px;
  padding: 0 24px;
  overflow: hidden;
  border-radius: 12px;
  background: var(--pt-row, #e2dcd7);
  color: var(--Neutral-UI-neutral-ui-100);
  cursor: pointer;
  // Mixed toward the theme's own ink: mixing toward a fixed dark ink moves a
  // dark surface by well under one step per channel, i.e. no visible hover.
  transition: background 200ms cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: color-mix(in srgb, var(--pt-row, #e2dcd7) 93%, var(--pt-hover-ink, #20282e));
  }

  // The hover mixes the row toward an ink that follows the theme, so it darkens in light mode and
  // lightens in dark. Either way it crosses the tile's own surface on the way, and where it lands
  // decides whether the row is still visible under the cursor.
  //
  // At 93% the dark one landed ON it: #373F46 against a tile painted #353F48, a contrast of 1.00,
  // so the row disappeared into the tile and read as a card gone transparent. The light one crosses
  // too and comes out the other side at the separation it had at rest, which is why only this
  // scheme needed correcting.
  //
  // 86% does the same for dark: the hovered row sits 1.23 from the tile, against the 1.228 the
  // resting row has — as far from the surface as it was, on the other side of it.
  @include dark-mode {
    &:hover {
      background: color-mix(in srgb, var(--pt-row, #e2dcd7) 86%, var(--pt-hover-ink, #20282e));
    }
  }

  // Only the arrow moves: a blanket svg rule also dragged the completed check
  // on the thumbnail and the one beside "Completed".
  &:hover .pr-go svg {
    transform: translateX(3px);
  }

  @include focus-visible;
}

// Finished programs sit one step deeper in the neutral scale. In dark mode the
// scale collapses the two together, exactly as the design does.
.pr-row--done {
  background: var(--pt-row-done, #cbc6c3);

  &:hover {
    background: color-mix(in srgb, var(--pt-row-done, #cbc6c3) 94%, var(--pt-hover-ink, #20282e));
  }

  // Same correction, and the same reason: dark mode collapses the finished row's surface into the
  // ordinary one, so it lands on the tile under the cursor exactly as that one did.
  @include dark-mode {
    &:hover {
      background: color-mix(in srgb, var(--pt-row-done, #cbc6c3) 86%, var(--pt-hover-ink, #20282e));
    }
  }
}

.pr-thumb {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 999px;
  background-color: var(--pt-sunk, #d0c9c3);
  background-position: center;
  background-size: cover;
}

// A finished program is marked on its own thumbnail: a teal veil with a check
// over the image.
.pr-thumb--done {
  position: relative;
  color: var(--today-ink-on-dark);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: color-mix(in srgb, var(--today-accent-complete) 60%, transparent);
  }

  svg {
    position: relative;
  }
}

// The design's own tracks: the progress block is a fixed width and the name and
// trailing columns split what is left, equally. The columns therefore depend on
// neither the name nor the label, so they line up down the whole tile. (Inside
// that block the bar still flexes a little with the label's length, as the
// design's own rules do.)
.pr-meta {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 24px;
  min-width: 0;
}

.pr-name {
  overflow: hidden;
  font-family: var(--Font-Secondary-Font);
  font-size: 24px;
  font-weight: 300;
  line-height: 21.6px;
  letter-spacing: -0.6px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pr-state {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 279px;
  font-family: var(--Font-Primary-Font);
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
}

.pr-done-label {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

// Teal in the design, not the surrounding ink — and fixed, like every other use
// of this green here.
.pr-check {
  flex-shrink: 0;
  color: var(--today-accent-complete);
}

.pr-bar {
  position: relative;
  flex-shrink: 1;
  overflow: hidden;
  width: 175px;
  // Never below 90: past that the bar stops reading as progress at all.
  min-width: 90px;
  max-width: 175px;
  height: 6px;
  border-radius: 100px;
  background: var(--pt-sunk, #d0c9c3);

  i {
    display: block;
    height: 100%;
    border-radius: 100px;
    background: var(--today-accent-complete);
  }
}

// The design separates the number from the word by more than a single space.
// Expressed as a gap rather than padding the translation with invisible
// characters, which collapse in HTML and read as a typo in the locale file.
.pr-pct {
  display: flex;
  gap: 7px;
  min-width: 88px;
  white-space: nowrap;
}

.pr-summary {
  // Hugs its text at the track's end, as the design does, but never exceeds the
  // track — a long translation truncates instead of pushing into its neighbour.
  //
  // A block and not a flex box, though it holds one line of text and either would
  // centre it: `text-overflow` is a property of a block container's inline content,
  // and inside a flex box the text is an anonymous item it never reaches. The pill
  // then had no ellipsis to fall back on and `overflow: hidden` cut the label mid-word.
  display: block;
  justify-self: end;
  max-width: 100%;
  padding: 8px 12px;
  text-align: center;
  border: 1px solid currentcolor;
  border-radius: 999px;
  font-family: var(--Font-Primary-Font);
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
  // Truncates instead of overflowing leftwards onto the status text: pinned to
  // the track's end, anything too wide would paint over its neighbour, which
  // "Rückblick ansehen" does in German.
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.pr-go {
  display: flex;
  justify-self: end;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 6px;
}

.pr-go svg {
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

// The design switches here — a viewport width of 1023, which with the tile
// spanning its row is about this much of the tile itself. Name and trailing
// control share the top line; the progress moves under them, full width.
@container program-tile (max-width: 975px) {
  .pr-row {
    align-items: start;
    gap: 8px;
    min-height: 0;
    padding: 8px 16px;
  }

  .pr-thumb {
    width: 34px;
    height: 34px;
  }

  .pr-meta {
    // The trailing track is capped: uncapped, a long label ("Rückblick
    // ansehen") takes the width it wants and the name shrinks to an ellipsis.
    grid-template-columns: minmax(0, 1fr) minmax(0, 40%);
    grid-template-areas:
      'name cta'
      'progress progress';
    gap: 6px 8px;
  }

  .pr-name {
    grid-area: name;
    align-self: center;
  }

  .pr-state {
    grid-area: progress;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
  }

  .pr-bar {
    width: 100%;
    max-width: 240px;
  }

  .pr-pct {
    min-width: 0;
  }

  .pr-go,
  .pr-summary {
    grid-area: cta;
    place-self: center end;
  }
}

</style>
